<script setup lang="ts">
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import TheIcon from '@/Components/TheIcon.vue'
import { Listing } from '../interfaces/listing'
import SpecialGuides from '@/Pages/the-city/components/SpecialGuides.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
import { Breadcrumb } from '@/Components/the-breadcrumbs/interfaces/Breadcrumb'
import { ContactActiveImports } from '../interfaces/contactActiveImports'
import { SimilarPlace } from '../interfaces/similarPlaces'
import { SpecialGuidesType } from '@/Pages/the-city/interfaces/City'

import 'swiper/css'

import SignupSidebar from '@/Components/SignupSidebar.vue'
import { User } from '@/types'

type Props = {
    pageContactActiveImports: ContactActiveImports
    pageListing: Listing
    pageSimilarPlaces: SimilarPlace[]
    pageBreadcrumbs: Breadcrumb[]
    pageSlps: SpecialGuidesType['slps'],
    hostelIsClosed: boolean,
    user: User | null
}

const props = defineProps<Props>()

const breakpointsSwiper = {
    '0': {
        slidesPerView: 1.5
    },
    '576': {
        slidesPerView: 1.8
    },
    '768': {
        slidesPerView: 2.2
    },
    '1200': {
        slidesPerView: 3
    }
}

const seeMoreHostels = computed(() => {
    const index = props.pageBreadcrumbs.length - 2
    return index >= 0 ? props.pageBreadcrumbs[index].url : null
})

const getBrandLinkClass = (systemShortName: string): string => {
    if (systemShortName === 'Hostelworld') {
        return 'btn-hw'
    } else if (systemShortName === 'Booking.com') {
        return 'btn-booking'
    } else {
        return 'listing-bottom-link'
    }
}
</script>

<template>
    <section class="listing-bottom">
        <div class="container">
            <div
                class="mt-3 mt-lg-5 d-flex flex-lg-row flex-column listing-bottom-content justify-content-between"
            >
                <div class="listing-bottom-content-left w-100">
                    <div class="listing-bottom-book-info pb-lg-5 mb-lg-5 pb-3 mb-3">
                        <h2 class="font-weight-bold sb-title listing-bottom-main-title mb-3">
                            How to book
                        </h2>
                        <p class="tx-body font-weight-600 mb-3">
                            {{ pageListing.name }} is listed on the following booking sites:
                        </p>
                        <div class="d-flex flex-column mb-lg-5 mb-3 listing-bottom-booking-sites">
                            <div
                                v-for="(link) in pageContactActiveImports"
                                :key="link.systemShortName"
                                class="d-flex justify-content-center"
                            >
                                <a
                                    v-if="link.isListed"
                                    :href="link.href"
                                    target="_blank"
                                    :title="`${pageListing.name} at ${link.systemShortName}`"
                                    class="btn w-100 d-flex align-items-center justify-content-center"
                                    :class="getBrandLinkClass(link.systemShortName)"

                                    rel="nofollow"
                                >
                                    <div
                                        v-if="link.systemShortName === 'Hostelworld'"
                                        class="position-relative d-flex align-items-center justify-content-center mr-2"
                                    >
                                        <TheIcon
                                            :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                            width="22"
                                            height="22"
                                            class="z-index-1 d-flex flex-shrink-0"
                                        />
                                        <div class="position-absolute hostelworld-icon-bg" />
                                    </div>
                                    <TheIcon
                                        v-else
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0"
                                    />


                                    {{ link.systemShortName }}

                                </a>
                                <a
                                    v-else
                                    :href="link.href"
                                    rel="nofollow"
                                    target="_blank"
                                    :title="`Not listed at ${ link.systemShortName }`"
                                    class="btn w-100 d-flex align-items-center justify-content-center"
                                    :class="[getBrandLinkClass(link.systemShortName), {'opacity-5': !link.isListed}]"
                                    :placeholder="`Not listed at ${link.systemShortName}`"
                                >
                                    <div
                                        v-if="link.systemShortName === 'Hostelworld'"
                                        class="position-relative d-flex align-items-center justify-content-center mr-2"
                                    >
                                        <TheIcon
                                            :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                            width="22"
                                            height="22"
                                            class="z-index-1 d-flex flex-shrink-0"
                                        />
                                        <div class="position-absolute hostelworld-icon-bg" />
                                    </div>
                                    <TheIcon
                                        v-else
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0"
                                    />
                                    <span class="font-weight-normal text-lowercase">
                                        Not listed at {{ link.systemShortName }}
                                    </span>
                                </a>
                            </div>
                        </div>


                        <div v-if="pageListing.hostelsChain" class="mb-3">
                            <p class="tx-body font-weight-600 mb-3">
                                {{ pageListing.name }} is part of the hostel chain
                                {{ pageListing.hostelsChain.name }}.
                            </p>

                            <div class="d-flex align-items-center">
                                <a
                                    :href="pageListing.hostelsChain.url"
                                    :title="pageListing.hostelsChain.name"
                                    target="_blank"
                                    rel=""
                                    class="btn w-100 d-flex align-items-center justify-content-center listing-bottom-link cl-text"
                                >
                                    <TheIcon
                                        icon-id="branch"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0"
                                        stroke="#4A5268"
                                    />

                                    <!-- <span class="listing-bottom-link-clink">Find all Clink Hostels here</span> -->
                                    <span class="listing-bottom-link-clink">{{
                                        pageListing.hostelsChain.name
                                    }}</span>
                                </a>
                            </div>
                        </div>

                        <!-- Todo: uncomment when they say  -->
                        <!-- <p v-if="!hostelIsClosed" class="tx-body font-weight-600 mb-3">
                            Website
                        </p>
                        <div v-if="!hostelIsClosed" class="mb-3">
                            <a
                                v-if="pageListing.website?.url"
                                :href="pageListing.website?.url"
                                target="_blank"
                                class="btn w-100 d-flex align-items-center justify-content-center listing-bottom-link cl-text"
                                rel="nofollow"
                            >
                                <TheIcon
                                    icon-id="red-earth"
                                    width="23"
                                    height="23"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                <span
                                    class="listing-bottom-link-no-website-link font-weight-bold text-uppercase text-break"
                                >{{
                                    pageListing.website?.name
                                }}</span>
                            </a>
                            <a
                                v-else
                                :href="route('listingCorrection', pageListing.id)"
                                target="_blank"
                                class="btn w-100 d-flex align-items-center justify-content-center listing-bottom-link cl-text"
                            >
                                <TheIcon
                                    icon-id="red-earth"
                                    width="23"
                                    height="23"
                                    class="mr-2 d-flex flex-shrink-0"
                                />
                                <span class="listing-bottom-link-no-website-link font-weight-normal ">(if you know the URL of their website, let us know and we'll add it) </span>
                            </a>
                        </div>
                        <div v-if="!hostelIsClosed" class="d-flex align-items-center listing-bottom-phone mb-3">
                            <span class="tx-body font-weight-600">
                                Phone number
                            </span>
                            <span v-if="pageListing.phone" class="cl-subtext">{{ pageListing.phone }}</span>
                            <a
                                v-else
                                href="#"
                                target="_blank"
                                title="let us know"
                            >Unknown (if you have their phone number, let us know and we'll add it)</a>
                        </div> -->


                        <p v-if="!hostelIsClosed" class="tx-body cl-subtext listing-bottom-give-data mb-0">
                            To add or correct information for this listing, please use the
                            <Link :href="route('listingCorrection', pageListing.id)" class="font-weight-600 cl-primary" rel="nofollow">
                                Listing Correction Form.
                            </Link>
                        </p>
                    </div>
                    
                    <div
                        v-if="pageSimilarPlaces.length"
                        class="listing-bottom-swiper-block pb-lg-5 mb-lg-5 pb-3 mb-3"
                    >
                        <h2 class="sb-title mb-3">
                            Similar places you might like
                        </h2>
                        <div class="listing-bottom-swiper">
                            <Swiper
                                :space-between="16"
                                :loop="pageSimilarPlaces.length > 3"
                                :autoplay="pageSimilarPlaces.length > 3 ? { delay: 0, disableOnInteraction: false} : {}"
                                :modules="pageSimilarPlaces.length > 3 ? [Autoplay] : []"
                                :speed="pageSimilarPlaces.length > 3 ? 7000 : 0"
                                class="listing-bottom-swiper"

                                :breakpoints="breakpointsSwiper"

                                grab-cursor
                                lazy
                            >
                                <SwiperSlide
                                    v-for="(slide, index) in pageSimilarPlaces"
                                    :key="index"
                                    class="listing-bottom-swiper-slide"
                                >
                                    <div class="listing-bottom-swiper-slide-wrap mb-3 mb-lg-5">
                                        <div class="listing-bottom-swiper-slide-container">
                                            <Link :href="slide.url" class="d-flex">
                                                <picture>
                                                    <source
                                                        :srcset="slide.pic.src.thumb_webp"
                                                        type="image/webp"
                                                    >
                                                    <source :srcset="slide.pic.src.thumb_def" type="image/jpg">

                                                    <img
                                                        :alt="slide.name"
                                                        :title="slide.name"
                                                        loading="lazy"
                                                    >
                                                </picture>
                                            </Link>
                                        </div>
                                        <div
                                            class="listing-bottom-swiper-slide-content d-flex flex-column justify-content-between p-3"
                                        >
                                            <div class="d-flex justify-content-between">
                                                <h4
                                                    v-if="slide.name"
                                                    class="listing-bottom-swiper-slide-title tx-body font-weight-600 mb-0 mr-2"
                                                >
                                                    <Link
                                                        :href="slide.url"
                                                        :title="slide.name"
                                                        class="text-decoration-none"
                                                    >
                                                        {{ slide.name }}
                                                    </Link>
                                                </h4>
                                                <div
                                                    v-if="slide.rating"
                                                    class="listing-bottom-swiper-slide-rating flex-shrink-0"
                                                >
                                                    <span class="pre-title">{{ slide.rating }}</span>
                                                </div>
                                            </div>
                                            <div
                                                v-if="slide.cityAlt"
                                                class="listing-top-neighborhood-inner mt-2 d-flex align-items-center"
                                            >
                                                <TheIcon icon-id="map-place" width="24" height="24" />

                                                <span
                                                    class="listing-top-neighborhood-title ml-2 pre-title cl-subtext"
                                                >{{
                                                    slide.cityAlt
                                                }}</span>
                                            </div>
                                            <p
                                                v-if="slide.snippet"
                                                class="listing-bottom-swiper-slide-text mt-2 truncate-text mb-0"
                                                v-html="slide.snippet"
                                            />

                                            <div
                                                v-if="slide.price"
                                                class="mt-2 listing-bottom-swiper-slide-price-wrap"
                                            >
                                                <p class="pre-title mb-0 listing-bottom-swiper-slide-price-text">
                                                    from
                                                </p>
                                                <span
                                                    class="listing-bottom-swiper-slide-price-sum font-weight-600"
                                                >
                                                    {{ slide.price }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="swiper-lazy-preloader" />
                                </SwiperSlide>
                            </Swiper>
                        </div>

                        <button
                            v-if="seeMoreHostels"
                            type="button"
                            class="listing-bottom-swiper-btn font-weight-bold d-flex"
                        >
                            <a
                                :href="seeMoreHostels"
                                target="_blank"
                                :title="`Hostels in ${pageListing.city}, ${pageListing.country}`"
                            >
                                See more
                            </a>
                        </button>
                    </div>
                </div>
                <div class="listing-bottom-content-right">
                    <SpecialGuides v-if="pageSlps?.length" :slps="pageSlps" />

                    <SignupSidebar />
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.listing-bottom {
    background-color: var(--light-bg);
    padding: 32px 0;

    @media (min-width: 992px) {
        padding: 64px 0;
    }

    &-book-info {
        border-bottom: 1px solid var(--accent-bg);
    }

    &-give-data {
        a {
            &:hover {
                border-bottom: 1px solid var(--primary-brand);
            }
        }
    }

    &-swiper {

        &:deep(.swiper-wrapper) {
            transition-timing-function: linear;
        }

        &-block {
            border-bottom: 1px solid var(--accent-bg);
        }

        &-btn {
            appearance: none;
            outline: none;
            border: none;
            padding: 0;

            &:focus {
                outline: none;
            }

            a {
                font-family: var(--font-family-poppins-sans-serif);
                font-size: 16px;
                line-height: 24px;
                letter-spacing: 0.03em;
                color: var(--light-bg);
                background-color: var(--main-text);
                text-decoration: none;
                text-transform: uppercase;
                transition: background-color 0.3s;
                padding: 12px 24px;

                box-shadow: 0px 6px 2px -4px #0E0E2C14;

                box-shadow: 0px -1px 0px 0px #0E0E2C3D inset;

                border-radius: 8px;

                &:hover {
                    background-color: black;
                }
            }
        }

        &-slide {

            picture {
                width: 100%;

                img {
                    object-fit: cover;
                    width: 100%;
                    height: 160px;

                    @media (min-width: 576px) {
                        height: 100%;
                        max-height: 180px;
                        min-height: 160px;
                    }
                }
            }

            &-wrap {
                border-radius: 16px;
                height: 405px;

                box-shadow: 0px 10px 2px -4px #0E0E2C14;

                background-color: white;

                @media (min-width: 576px) {
                    height: 425px;
                }
            }

            &-rating {
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: var(--main-text);
                border-radius: 50%;
                width: 25px;
                height: 25px;

                span {
                    color: var(--light-bg);
                }
            }

            &-container {
                img {
                    border-radius: 16px 16px 0 0;
                }
            }

            &-content {
                height: calc(100% - 160px);

                @media (min-width: 576px) {
                    height: calc(100% - 180px);
                }

                @media (min-width: 992px) {
                    height: calc(100% - 160px);
                }

                @media (min-width: 1400px) {
                    height: calc(100% - 180px);
                }
            }

            &-text {
                font-family: var(--font-family-poppins-sans-serif);
                font-size: 14px;
                font-weight: 400;
                line-height: 1.4;
                color: var(--secondary-text);

                &:deep(*) {
                    font-family: var(--font-family-poppins-sans-serif);
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 1.4;
                    color: var(--secondary-text);
                }
            }

            &-price {
                &-text {
                    color: #8C91A1;
                }
            }
        }

    }

    &-link {
        background-color: var(--light-bg);
        padding: 11px;
        border: 1px solid var(--accent-bg);
        border-radius: 8px;
        box-shadow: none;
        color: white;
        transition: opacity 0.3s;

        &:hover {
            background-color: #e2e6ea;
            opacity: 0.7;
        }

        &-no-website-link {
            text-transform: lowercase;
            text-decoration: none;
        }
    }

    &-booking-sites {
        gap: 16px;
    }

    &-phone {
        gap: 16px;
    }

    &-content {
        gap: 32px;

        @media (min-width: 1200px) {
            gap: 64px;
        }

        &-left {
            @media (min-width: 992px) {
                max-width: calc(100% - 360px - 32px);
            }

            @media (min-width: 1200px) {
                max-width: calc(100% - 360px - 64px);
            }
        }

        &-right {

            @media (min-width: 992px) {
                max-width: 360px;
                flex-shrink: 0;
            }

            &:deep(.toggle-section) {
                pointer-events: none;

                h2 {
                    font-family: var(--font-family-poppins-sans-serif);
                    font-style: normal;
                    font-weight: 600;
                    font-size: 24px;
                    line-height: 36px;
                }

                svg {
                    display: none;
                }
            }
        }
    }

    &-header {
        font-family: var(--font-family-montserrat-sans-serif);
    }

    &-main-title {
        font-family: var(--font-family-montserrat-sans-serif);
    }
}

.truncate-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    display: -moz-box;
    -moz-box-orient: vertical;
    -moz-line-clamp: 3;
    line-clamp: 3;
    text-overflow: ellipsis;
}

.hostelworld-icon-bg {
    background-color: white;
    width: 16px;
    height: 16px;
    z-index: 0;
}
</style>
